import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchPacks } from '../../store/pricingSlice';
import { fetchPrestations } from '../../store/prestationSlice';
import { pricingService } from '../../services/api';
import type { PricingPack, Prestation } from '../../types';

const PackPrestations: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { packs } = useAppSelector((state) => state.pricing);
  const { prestations, loading } = useAppSelector((state) => state.prestation);
  
  // Determine active tab based on current route
  const getTabValue = () => {
    if (location.pathname.includes('/prestations/pricing')) return 1;
    if (location.pathname.includes('/prestations/packs')) return 2;
    return 0;
  };
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/prestations');
        break;
      case 1:
        navigate('/prestations/pricing');
        break;
      case 2:
        navigate('/prestations/packs');
        break;
    }
  };
  
  const [selectedPack, setSelectedPack] = useState<number | ''>('');
  const [selectedPrestations, setSelectedPrestations] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    dispatch(fetchPacks());
    dispatch(fetchPrestations());
  }, [dispatch]);

  useEffect(() => {
    if (selectedPack) {
      loadPackPrestations();
    } else {
      setSelectedPrestations([]);
    }
  }, [selectedPack]);

  const loadPackPrestations = async () => {
    if (!selectedPack) return;
    try {
      const response = await pricingService.getPackPrestations(selectedPack as number);
      setSelectedPrestations(response.data.data.map((p: Prestation) => p.id));
    } catch (error) {
      console.error('Error loading pack prestations:', error);
      setError('Erreur lors du chargement des prestations');
    }
  };

  const handlePackChange = (packId: number | '') => {
    setSelectedPack(packId);
    setError(null);
    setSuccess(false);
  };

  const handlePrestationToggle = (prestationId: number) => {
    setSelectedPrestations((prev) => {
      if (prev.includes(prestationId)) {
        return prev.filter((id) => id !== prestationId);
      } else {
        return [...prev, prestationId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedPack) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await pricingService.syncPackPrestations(selectedPack as number, selectedPrestations);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const selectedPackData = packs.find((p) => p.id === selectedPack);

  const prestationsByType = {
    OPTION: prestations.filter((p) => p.type === 'OPTION'),
    ASSURANCE: prestations.filter((p) => p.type === 'ASSURANCE'),
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={getTabValue()} onChange={handleTabChange}>
          <Tab label="Prestations" />
          <Tab label="Tarification" />
          <Tab label="Prestations des Packs" />
        </Tabs>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Prestations des Packs</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Prestations mises à jour avec succès
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Pack</InputLabel>
            <Select
              value={selectedPack}
              onChange={(e) => handlePackChange(e.target.value as number | '')}
              label="Pack"
            >
              <MenuItem value="">Sélectionnez un pack</MenuItem>
              {packs.map((pack) => (
                <MenuItem key={pack.id} value={pack.id}>
                  {pack.name} - {pack.category?.name || '-'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {selectedPack && selectedPackData && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6">{selectedPackData.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPackData.category?.name || '-'} - Plan: {selectedPackData.plan?.name || '-'}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Enregistrer
              </Button>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Sélectionnez les prestations incluses dans ce pack (gratuites pour le client)
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Options */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Options
                    </Typography>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : prestationsByType.OPTION.length === 0 ? (
                      <Typography color="text.secondary">Aucune option disponible</Typography>
                    ) : (
                      <Box>
                        {prestationsByType.OPTION.map((prestation) => (
                          <FormControlLabel
                            key={prestation.id}
                            control={
                              <Checkbox
                                checked={selectedPrestations.includes(prestation.id)}
                                onChange={() => handlePrestationToggle(prestation.id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body1">{prestation.name}</Typography>
                                {prestation.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {prestation.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ display: 'flex', mb: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Assurances */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Assurances
                    </Typography>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : prestationsByType.ASSURANCE.length === 0 ? (
                      <Typography color="text.secondary">Aucune assurance disponible</Typography>
                    ) : (
                      <Box>
                        {prestationsByType.ASSURANCE.map((prestation) => (
                          <FormControlLabel
                            key={prestation.id}
                            control={
                              <Checkbox
                                checked={selectedPrestations.includes(prestation.id)}
                                onChange={() => handlePrestationToggle(prestation.id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body1">{prestation.name}</Typography>
                                {prestation.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {prestation.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ display: 'flex', mb: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {selectedPrestations.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Prestations sélectionnées ({selectedPrestations.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedPrestations.map((id) => {
                    const prestation = prestations.find((p) => p.id === id);
                    return prestation ? (
                      <Chip
                        key={id}
                        label={prestation.name}
                        color="primary"
                        variant="outlined"
                        onDelete={() => handlePrestationToggle(id)}
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedPack && (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              Sélectionnez un pack pour gérer ses prestations incluses
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PackPrestations;
